import { useRef, type MouseEvent, type ReactNode } from "react";
import type { PluggableList } from "unified";
import { useTranslation } from "react-i18next";
import { messageText } from "../lib/chatNotice";
import { ONTOLOGY_TYPE_ICON } from "../lib/ontologyTypeIcons";
import type {
  AssistantMessage,
  AssistantMessageIdentifier,
  AssistantReference,
} from "../types/assistant";
import { AnswerMarkdown } from "./AnswerMarkdown";
import { chatRating } from "./AnswerReview";
import { AssistantMessageAttachments } from "./AssistantMessageAttachments";
import { MessageReferences, type MessageReferencesProps } from "./MessageReferences";
import { ToolCallList } from "./ToolCallList";
import {
  ChatAssistantMessage,
  ChatChoices,
  ChatUserMessage,
  type ChatAssistantMessageLabels,
  type ChatFeedback,
  type ChatRating,
} from "./v2/ChatMessage";
import { ChatThread, type ChatThreadItem, type ChatThreadLabels } from "./v2/ChatThread";
import { Chip, ChipGroup } from "./v2/ui/chip";
import { Logo } from "./v2/ui/logo";

// The brand mark as a vector, centred in the 24px avatar column: the image file in a
// round avatar crops it.
const LYTRA_AVATAR = (
  <span className="flex size-6 items-center justify-center">
    <Logo variant="mark" className="h-4 w-auto" />
  </span>
);

/** The operator's full name, for screen readers; none for a customer. */
function authorName(message: AssistantMessage): string | undefined {
  const name = [message.agent?.givenName, message.agent?.familyName].filter(Boolean).join(" ");
  return name || undefined;
}

/** A chip's text: the pattern's label ("Maschinennummer") or its kind, then the value. */
function chipTitle(ident: AssistantMessageIdentifier): string {
  const base = ident.label?.trim() || ident.attributeKey;
  return `${base} ${ident.value}`;
}

/** The identifiers the backend found in a user's message, as chips; a link when the
 * tenant's pattern has a URL template. A kind the TBox backs shows its type's icon. */
function IdentifierChips({ identifiers }: { identifiers: AssistantMessageIdentifier[] }) {
  return (
    <ChipGroup className="justify-end">
      {identifiers.map((ident) =>
        ident.externalLink ? (
          <Chip
            key={ident.id}
            asChild
            label={chipTitle(ident)}
            icon={ONTOLOGY_TYPE_ICON[ident.attributeKey]}
            numeric
          >
            <a href={ident.externalLink} target="_blank" rel="noopener noreferrer" />
          </Chip>
        ) : (
          <Chip
            key={ident.id}
            label={chipTitle(ident)}
            icon={ONTOLOGY_TYPE_ICON[ident.attributeKey]}
            numeric
          />
        ),
      )}
    </ChipGroup>
  );
}

/** The chat user's own rating of an answer they were given, on the answer's thumbs. */
export function ownFeedback(
  message: AssistantMessage,
  onSave: (messageId: string, rating: ChatRating, comment: string) => void,
): ChatFeedback {
  return {
    value: chatRating(message.feedback),
    comment: message.feedbackComment,
    onSave: (rating, comment) => onSave(message.id, rating, comment),
  };
}

export interface AssistantMessageFeedProps {
  /** Persisted messages, oldest first (no progress messages). */
  displayMessages: AssistantMessage[];
  /** The in-flight turn's progress message (no createdAt). */
  progressMessage?: AssistantMessage;
  isWaitingForResponse: boolean;
  responseTimedOut?: boolean;
  /** The conversation is being created: the sent message is shown, an answer pending. */
  isCreatingConversation?: boolean;
  /** `md` for a page, `sm` for a side panel or the add-in drawer. */
  size?: "md" | "sm";
  /** Follow new content while near the bottom. False pauses it (the add-in's fact-fly). */
  autoScroll?: boolean;
  /** Who answers in a customer chat (the tenant); "lytra" otherwise. */
  assistantName?: string;
  /** The reader's rating of each answer, on its thumbs (`ownFeedback` for the chat user's
   * own); without it a feed shows no thumbs. */
  feedback?: (message: AssistantMessage) => ChatFeedback;
  /** Shows each answer's sources, with these callbacks; without it, none. */
  references?: Omit<MessageReferencesProps, "references">;
  /** Return rehype plugins for a given message. Called once per AI message. */
  buildRehypePlugins?: (
    messageId: string,
    references: AssistantReference[] | undefined,
  ) => PluggableList;
  /** A choice on the latest answer was picked; the click is passed for hosts that
   * need its point (the fact-fly origin). */
  onChoiceSelect?: (option: string, event: MouseEvent<HTMLButtonElement>, message: AssistantMessage) => void;
  /**
   * Show the "Für Antwort" toggle on attachment chips. Only meaningful where a
   * drafted reply exists to regenerate (ticket assistant), not on the standalone
   * chat page.
   */
  showAttachmentUseInAnswer?: boolean;
  /** Rendered under each answer, below its thumbs. */
  answerFooter?: (message: AssistantMessage) => ReactNode;
  /** Shown while there are no messages. */
  empty?: ReactNode;
  className?: string;
}

/** An assistant conversation as a v2 ChatThread: the messages mapped onto the message
 * parts, with the running turn as one pending answer at the end. The thread scrolls. */
export function AssistantMessageFeed({
  displayMessages,
  progressMessage,
  isWaitingForResponse,
  responseTimedOut = false,
  isCreatingConversation = false,
  size = "md",
  autoScroll = true,
  assistantName,
  feedback,
  references,
  buildRehypePlugins,
  onChoiceSelect,
  showAttachmentUseInAnswer = false,
  answerFooter,
  empty,
  className,
}: AssistantMessageFeedProps) {
  const { t } = useTranslation();
  const author = assistantName ?? "lytra";
  // A customer chat answers as the tenant: its initial, not the lytra mark.
  const avatar = assistantName ? undefined : LYTRA_AVATAR;
  const waiting = isWaitingForResponse || isCreatingConversation;

  const assistantLabels: ChatAssistantMessageLabels = {
    author,
    thinking: t("waitingIndicator.thinking"),
    copy: t("chatThread.copy", { defaultValue: "Kopieren" }),
    copied: t("chatThread.copied", { defaultValue: "Kopiert" }),
    copyFailed: t("chatThread.copyFailed", { defaultValue: "Kopieren fehlgeschlagen" }),
    regenerate: t("chatThread.regenerate", { defaultValue: "Neu generieren" }),
    error: t("chatThread.error", {
      defaultValue: "Die Antwort konnte nicht erstellt werden. Bitte versuchen Sie es erneut.",
    }),
    stopped: t("chatThread.stopped", { defaultValue: "Antwort gestoppt." }),
    retry: t("chatThread.retry", { defaultValue: "Erneut versuchen" }),
    helpful: t("chatThread.helpful", { defaultValue: "Hilfreich" }),
    notHelpful: t("chatThread.notHelpful", { defaultValue: "Nicht hilfreich" }),
    feedbackComment: t("chatThread.feedbackComment", {
      defaultValue: "Was war gut oder falsch? (optional)",
    }),
    saveFeedbackComment: t("chatThread.saveFeedbackComment", { defaultValue: "Speichern" }),
    cancelFeedbackComment: t("chatThread.cancelFeedbackComment", { defaultValue: "Abbrechen" }),
  };
  const threadLabels: ChatThreadLabels = {
    region: t("chatThread.region", { defaultValue: "Nachrichtenverlauf" }),
    today: t("chatThread.today", { defaultValue: "Heute" }),
    yesterday: t("chatThread.yesterday", { defaultValue: "Gestern" }),
    jumpToLatest: t("chatThread.jumpToLatest", { defaultValue: "Zu neuen Nachrichten" }),
    newAnswer: t("chatThread.newAnswer", { author, defaultValue: `Neue Antwort von ${author}` }),
    loadingOlder: t("chatThread.loadingOlder", { defaultValue: "Ältere Nachrichten werden geladen" }),
  };
  const youLabel = t("chatThread.you", { defaultValue: "Sie" });
  const choicesLabels = { group: t("chatThread.choices", { defaultValue: "Antwortvorschläge" }) };

  // Stamped when the wait starts, so the pending answer's time holds still.
  const pendingSince = useRef<Date | null>(null);
  if (!waiting) pendingSince.current = null;
  else pendingSince.current ??= new Date();

  const lastAnswer = [...displayMessages].reverse().find((m) => !m.isFromUser && m.createdAt);

  const items: ChatThreadItem[] = displayMessages.map((message) => {
    const time = new Date(message.createdAt ?? Date.now());
    if (message.isFromUser) {
      const identifiers = message.identifiers ?? [];
      const chips =
        identifiers.length > 0 || (message.attachments?.length ?? 0) > 0 ? (
          <div className="flex flex-col items-end gap-1">
            {identifiers.length > 0 && <IdentifierChips identifiers={identifiers} />}
            <AssistantMessageAttachments
              attachments={message.attachments}
              compact={size === "sm"}
              showUseInAnswer={showAttachmentUseInAnswer}
            />
          </div>
        ) : undefined;
      return {
        id: message.id,
        author: "user",
        time,
        content: (
          <ChatUserMessage
            layout="row"
            text={message.text}
            time={time}
            chips={chips}
            // Screen readers name each message; on screen the side says who wrote it.
            labels={{ author: authorName(message) ?? youLabel }}
          />
        ),
      };
    }

    const text = messageText(message, t);
    const latest = message.id === lastAnswer?.id;
    const answer = (
      <ChatAssistantMessage
        size={size}
        time={time}
        text={text}
        copyText={text}
        avatar={avatar}
        steps={
          message.toolCalls && message.toolCalls.length > 0 ? (
            <ToolCallList toolCalls={message.toolCalls} />
          ) : undefined
        }
        sources={
          references && message.references && message.references.length > 0 ? (
            <MessageReferences references={message.references} {...references} />
          ) : undefined
        }
        choices={
          onChoiceSelect && message.choiceOptions && message.choiceOptions.length > 0 ? (
            <ChatChoices
              options={message.choiceOptions}
              onSelect={(value, event) => onChoiceSelect(value, event, message)}
              disabled={waiting || !latest}
              labels={choicesLabels}
            />
          ) : undefined
        }
        feedback={feedback?.(message)}
        actionsVisible={latest}
        labels={assistantLabels}
      >
        {text ? (
          <AnswerMarkdown text={text} rehypePlugins={buildRehypePlugins?.(message.id, message.references)} />
        ) : undefined}
      </ChatAssistantMessage>
    );
    const footer = answerFooter?.(message);
    return {
      id: message.id,
      author: "assistant",
      time,
      content: footer ? (
        <div className="flex flex-col gap-2">
          {answer}
          {/* Lined up with the answer's text, past the avatar column. */}
          <div className="pl-8">{footer}</div>
        </div>
      ) : (
        answer
      ),
    };
  });

  if (waiting) {
    // One id for the whole wait: the persisted answer replaces it under its own id,
    // and that new id is what the thread announces.
    const time = pendingSince.current ?? new Date();
    if (responseTimedOut) {
      items.push({
        id: "pending",
        author: "assistant",
        time,
        state: "error",
        content: (
          <ChatAssistantMessage
            size={size}
            state="error"
            time={time}
            avatar={avatar}
            labels={{
              ...assistantLabels,
              error: t("chatThread.timeout", {
                defaultValue: "Zeitüberschreitung. Bitte versuchen Sie es erneut.",
              }),
            }}
          />
        ),
      });
    } else {
      const before = progressMessage?.toolCalls ?? [];
      const after = progressMessage?.toolCallsAfterText ?? [];
      const text = progressMessage?.text ?? "";
      items.push({
        id: "pending",
        author: "assistant",
        time,
        state: "pending",
        content: (
          <ChatAssistantMessage
            size={size}
            state={text ? "streaming" : "waiting"}
            time={time}
            avatar={avatar}
            steps={
              before.length > 0 ? (
                // Only the list the turn is working on is live: once text streams,
                // the earlier steps are done.
                <ToolCallList toolCalls={before} live={!text && after.length === 0} />
              ) : undefined
            }
            stepsAfter={after.length > 0 ? <ToolCallList toolCalls={after} live /> : undefined}
            labels={assistantLabels}
          >
            {text
              ? (
                <AnswerMarkdown
                  text={text}
                  rehypePlugins={buildRehypePlugins?.(progressMessage?.id ?? "pending", progressMessage?.references)}
                />
              )
              : undefined}
          </ChatAssistantMessage>
        ),
      });
    }
  }

  return (
    <ChatThread
      className={className}
      size={size}
      autoScroll={autoScroll}
      items={items}
      empty={empty}
      labels={threadLabels}
    />
  );
}
